import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import LanguageCurrencySwitcher from '../components/LanguageCurrencySwitcher';
import Logo from '../components/Logo';
import {
  ShieldCheck,
  Lightning,
  Robot,
  LinkBreak,
  Image as ImageIcon,
  ChartLineUp,
  CheckCircle,
  Sparkle,
  ArrowRight,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function LandingPage() {
  const { t } = useTranslation();
  const [form, setForm] = useState({ email: '', name: '', shop_url: '', product_count: '', tier_interest: 'pro', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email) return;
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/api/marketing/waitlist`, form);
      toast.success(data.message);
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setSubmitting(false);
    }
  };

  const features = [
    { icon: Robot, title: t('features.autoguard_title'), desc: t('features.autoguard_desc') },
    { icon: Sparkle, title: t('features.seo_title'), desc: t('features.seo_desc') },
    { icon: LinkBreak, title: t('features.links_title'), desc: t('features.links_desc') },
    { icon: ImageIcon, title: t('features.media_title'), desc: t('features.media_desc') },
    { icon: ChartLineUp, title: t('features.diff_title'), desc: t('features.diff_desc') },
    { icon: ShieldCheck, title: t('features.compat_title'), desc: t('features.compat_desc') },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="landing-page">
      <nav className="border-b border-border/50 backdrop-blur-sm bg-background/80 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Logo variant="full" height={36} data-testid="nav-logo" />
          </div>
          <div className="flex items-center gap-3">
            <Link to="/pricing" className="text-sm hover:text-primary" data-testid="nav-pricing">{t('nav.pricing')}</Link>
            <a href="#features" className="text-sm hover:text-primary hidden sm:inline">{t('nav.features')}</a>
            <LanguageCurrencySwitcher />
            <Link to="/login">
              <Button variant="outline" size="sm" data-testid="nav-login">{t('nav.login')}</Button>
            </Link>
          </div>
        </div>
      </nav>

      <section className="max-w-7xl mx-auto px-6 py-24 grid md:grid-cols-2 gap-12 items-center">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary/10 border border-primary/20 rounded-full text-xs font-semibold text-primary mb-6">
            <Lightning size={12} weight="fill" /> {t('hero.badge')}
          </div>
          <h1 className="font-heading text-5xl lg:text-6xl font-extrabold tracking-tight leading-tight">
            {t('hero.title_1')}<span className="text-primary">{t('hero.title_highlight')}</span>{t('hero.title_2')}
          </h1>
          <p className="text-lg text-muted-foreground mt-6 leading-relaxed">{t('hero.subtitle')}</p>
          <div className="flex gap-3 mt-8">
            <a href="#waitlist">
              <Button size="lg" className="gap-2" data-testid="hero-cta-waitlist">
                {t('hero.cta_waitlist')} <ArrowRight size={16} weight="bold" />
              </Button>
            </a>
            <Link to="/pricing">
              <Button size="lg" variant="outline" data-testid="hero-cta-pricing">{t('hero.cta_pricing')}</Button>
            </Link>
          </div>
          <div className="flex gap-6 mt-8 text-sm text-muted-foreground flex-wrap">
            <div className="flex items-center gap-1"><CheckCircle size={14} className="text-success" /> {t('hero.trust_1')}</div>
            <div className="flex items-center gap-1"><CheckCircle size={14} className="text-success" /> {t('hero.trust_2')}</div>
            <div className="flex items-center gap-1"><CheckCircle size={14} className="text-success" /> {t('hero.trust_3')}</div>
          </div>
        </div>

        <Card className="border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
          <CardContent className="p-8">
            <p className="italic text-muted-foreground leading-relaxed">"{t('testimonial.quote')}"</p>
            <div className="flex items-center justify-between mt-6 pt-6 border-t border-border">
              <span className="text-sm font-semibold">{t('testimonial.author')}</span>
              <div className="text-right">
                <p className="font-heading text-2xl font-extrabold text-primary">7 220</p>
                <p className="text-xs text-muted-foreground">{t('testimonial.stat_label')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </section>

      <section id="features" className="bg-muted/20 py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-primary mb-3">{t('features.section_label')}</p>
            <h2 className="font-heading text-4xl font-extrabold tracking-tight">{t('features.section_title')}</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {features.map((f) => (
              <Card key={f.title} className="border border-border hover:border-primary/40 transition-colors">
                <CardContent className="p-6">
                  <f.icon size={28} weight="bold" className="text-primary mb-3" />
                  <h3 className="font-heading text-lg font-bold mb-2">{f.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 border-y border-border/50">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-4 gap-8 text-center">
          {[
            { n: '7 220', l: t('stats.products') },
            { n: '103', l: t('stats.categories') },
            { n: '6 003', l: t('stats.terms') },
            { n: '< 5 min', l: t('stats.daily') },
          ].map((s) => (
            <div key={s.l}>
              <p className="font-heading text-4xl font-extrabold text-primary">{s.n}</p>
              <p className="text-sm text-muted-foreground mt-1">{s.l}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="waitlist" className="py-24">
        <div className="max-w-xl mx-auto px-6">
          <div className="text-center mb-8">
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-primary mb-3">{t('waitlist.label')}</p>
            <h2 className="font-heading text-4xl font-extrabold tracking-tight">{t('waitlist.title')}</h2>
            <p className="text-muted-foreground mt-3">{t('waitlist.subtitle')}</p>
          </div>

          {submitted ? (
            <Card className="border-success/30 bg-success/5" data-testid="waitlist-success">
              <CardContent className="p-8 text-center">
                <CheckCircle size={40} weight="fill" className="text-success mx-auto mb-3" />
                <h3 className="font-heading text-xl font-bold mb-2">{t('waitlist.success_title')}</h3>
                <p className="text-sm text-muted-foreground">{t('waitlist.success_subtitle')}</p>
              </CardContent>
            </Card>
          ) : (
            <Card className="border border-border">
              <CardContent className="p-6">
                <form onSubmit={submit} className="space-y-3" data-testid="waitlist-form">
                  <div className="grid grid-cols-2 gap-3">
                    <Input placeholder={t('waitlist.name')} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="waitlist-name" />
                    <Input type="email" placeholder={t('waitlist.email')} required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="waitlist-email" />
                  </div>
                  <Input placeholder={t('waitlist.shop_url')} value={form.shop_url} onChange={(e) => setForm({ ...form, shop_url: e.target.value })} data-testid="waitlist-shop" />
                  <div className="grid grid-cols-2 gap-3">
                    <Select value={form.product_count} onValueChange={(v) => setForm({ ...form, product_count: v })}>
                      <SelectTrigger data-testid="waitlist-product-count"><SelectValue placeholder={t('waitlist.product_count')} /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0-500">0 – 500</SelectItem>
                        <SelectItem value="500-2000">500 – 2 000</SelectItem>
                        <SelectItem value="2000-10000">2 000 – 10 000</SelectItem>
                        <SelectItem value="10000+">10 000+</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={form.tier_interest} onValueChange={(v) => setForm({ ...form, tier_interest: v })}>
                      <SelectTrigger data-testid="waitlist-tier"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="starter">{t('waitlist.tier_starter')}</SelectItem>
                        <SelectItem value="pro">{t('waitlist.tier_pro')}</SelectItem>
                        <SelectItem value="agency">{t('waitlist.tier_agency')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Textarea placeholder={t('waitlist.message_placeholder')} rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="waitlist-message" />
                  <Button type="submit" size="lg" className="w-full gap-2" disabled={submitting} data-testid="waitlist-submit">
                    {submitting ? t('waitlist.submitting') : t('waitlist.submit')}
                    {!submitting && <ArrowRight size={16} />}
                  </Button>
                </form>
              </CardContent>
            </Card>
          )}
        </div>
      </section>

      <footer className="border-t border-border py-10">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-3">
            <Logo variant="icon" height={24} />
            <span data-testid="footer-copyright">
              © {new Date().getFullYear()} Sortilize. {t('footer.tagline')} · Laget av{' '}
              <a
                href="https://holmsendesign.no"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-foreground/80 hover:text-primary transition-colors"
                data-testid="footer-holmsen-link"
              >
                Holmsen design
              </a>
            </span>
          </div>
          <div className="flex gap-6">
            <Link to="/pricing" className="hover:text-primary">{t('nav.pricing')}</Link>
            <a href="#waitlist" className="hover:text-primary">{t('nav.waitlist')}</a>
            <Link to="/login" className="hover:text-primary">{t('nav.login')}</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
